import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useApiClient } from "@/api/useApiClient";
import { useToast } from "@/components/ui";
import { createSupportApi } from "@/modules/support/api";
import { createBusinessesApi } from "@/modules/businesses/api";
import { createDeskApi, REASONS, SUBJECT_LABEL } from "@/modules/trust-safety/desk/deskApi";
import { desktopNotify, playAlertChime, subscribeStaffSignals } from "./staffSignals";

/**
 * What needs a reviewer's attention, noticed while HQ is open.
 *
 * Three sources, each compared with the last look: open support tickets (and
 * new customer replies on them), submitted business applications, and — since
 * 2026-10-01 — open Trust & Safety reports. Anything new becomes an entry in
 * the Notifications list, a pop-up, a chime, a count in the tab title and, when
 * the tab is in the background and the reviewer allowed it, a desktop
 * notification. High-priority reports sort to the top and sound different.
 *
 * ── Live, with polling underneath ───────────────────────────────────────────
 * The database broadcasts a content-free signal the moment a ticket, a
 * customer message or a report is written (i67, private Realtime topics
 * gated by the reviewer's own permissions). A signal triggers an immediate
 * re-read through the API. The 15 s poll stays as the safety net and, unlike
 * page refreshes, keeps running in a background tab — an alert that waits for
 * the reviewer to come back to the tab is not an alert.
 *
 * ── Three rules that keep it honest ─────────────────────────────────────────
 *  1. The FIRST successful look at each source only records what exists.
 *     Alerting on everything already queued would greet a new reviewer with
 *     fifty pop-ups about old work.
 *  2. What has been seen survives a reload (localStorage).
 *  3. Each source fails alone. A reviewer without Trust & Safety access still
 *     gets support alerts, and a failed poll keeps the list already shown.
 */

const STORAGE_KEY = "hq.staff-alerts.v2";
const MAX_ALERTS = 60;
const POLL_MS = 15_000;
/** Statuses where a ticket is waiting on staff rather than on the customer. */
const NEEDS_STAFF = "new,open,assigned,pending_internal_resolution,escalated,reopened";

export type StaffAlertKind = "ticket" | "reply" | "application" | "report";

export interface StaffAlert {
  /** Stable per event, so the same event is never listed twice. */
  key: string;
  kind: StaffAlertKind;
  title: string;
  detail: string;
  /** In-app route for "open this". */
  to: string;
  at: number;
  read: boolean;
  /** low / normal / high / urgent / critical, when the source has one. */
  priority?: string;
}

interface Snapshot {
  /** Per source: false until its first successful look recorded the queue. */
  primed: { tickets: boolean; applications: boolean; reports: boolean };
  /** ticket id → last customer-message time it had when last seen ("" = none). */
  tickets: Record<string, string>;
  applications: string[];
  reports: string[];
  alerts: StaffAlert[];
}

const EMPTY: Snapshot = {
  primed: { tickets: false, applications: false, reports: false },
  tickets: {},
  applications: [],
  reports: [],
  alerts: [],
};

const PRIORITY_RANK: Record<string, number> = { critical: 4, urgent: 3, high: 2, normal: 1, low: 0 };
export const priorityRank = (p?: string) => (p ? PRIORITY_RANK[p] ?? 1 : 1);
export const isUrgent = (a: Pick<StaffAlert, "priority">) => priorityRank(a.priority) >= 2;

/** Unread first; within that, highest priority first; then newest. */
export function sortAlerts(list: StaffAlert[]): StaffAlert[] {
  return [...list].sort((a, b) =>
    Number(a.read) - Number(b.read)
    || priorityRank(b.priority) - priorityRank(a.priority)
    || b.at - a.at);
}

function load(): Snapshot {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) {
      // Carry over what v1 had seen, so upgrading does not re-announce it.
      const old = window.localStorage.getItem("hq.staff-alerts.v1");
      if (!old) return EMPTY;
      const v1 = JSON.parse(old) as { primed?: boolean; tickets?: Record<string, string>; applications?: string[]; alerts?: StaffAlert[] };
      return {
        primed: { tickets: !!v1.primed, applications: !!v1.primed, reports: false },
        tickets: v1.tickets ?? {},
        applications: v1.applications ?? [],
        reports: [],
        alerts: v1.alerts ?? [],
      };
    }
    const parsed = JSON.parse(raw) as Partial<Snapshot>;
    return {
      primed: { ...EMPTY.primed, ...(parsed.primed ?? {}) },
      tickets: parsed.tickets ?? {},
      applications: parsed.applications ?? [],
      reports: parsed.reports ?? [],
      alerts: parsed.alerts ?? [],
    };
  } catch (e) {
    console.warn("[staff-alerts] could not read saved alerts", e);
    return EMPTY;
  }
}

function save(snapshot: Snapshot) {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(snapshot));
  } catch (e) {
    console.warn("[staff-alerts] could not save alerts", e);
  }
}

const reasonLabel = (code: string | null | undefined) =>
  REASONS.find((r) => r.value === code)?.label ?? "Report";

export function useStaffAlerts() {
  const client = useApiClient();
  const navigate = useNavigate();
  const supportApi = useMemo(() => createSupportApi(client), [client]);
  const businessesApi = useMemo(() => createBusinessesApi(client), [client]);
  const deskApi = useMemo(() => createDeskApi(client), [client]);
  const toast = useToast();

  const snapshot = useRef<Snapshot>(load());
  const [alerts, setAlerts] = useState<StaffAlert[]>(sortAlerts(snapshot.current.alerts));
  const polling = useRef(false);
  const again = useRef(false);

  const commit = useCallback((next: Snapshot) => {
    snapshot.current = next;
    save(next);
    setAlerts(sortAlerts(next.alerts));
  }, []);

  const poll = useCallback(async () => {
    // A slow poll must not stack a second one behind it — but a signal that
    // arrives mid-poll must not be lost either, so it queues exactly one more.
    if (polling.current) { again.current = true; return; }
    polling.current = true;
    try {
      const prev = snapshot.current;
      const fresh: StaffAlert[] = [];
      const primed = { ...prev.primed };
      let tickets = prev.tickets;
      let applications = prev.applications;
      let reports = prev.reports;

      // ── Support ──
      try {
        const list = await supportApi.listTickets({ status: NEEDS_STAFF, limit: 50 });
        const next = { ...prev.tickets };
        for (const t of list.tickets) {
          const lastReply = t.last_customer_message_at ?? "";
          const known = t.id in prev.tickets;
          if (!known) {
            fresh.push({
              key: `ticket:${t.id}`,
              kind: "ticket",
              title: `New support ticket ${t.public_id}`,
              detail: t.contact_reason?.trim() || "A player is asking for help.",
              to: `/app/support/tickets/${t.id}`,
              at: Date.now(),
              read: false,
              ...(t.priority ? { priority: t.priority } : {}),
            });
          } else if (lastReply && lastReply > (prev.tickets[t.id] ?? "")) {
            fresh.push({
              key: `reply:${t.id}:${lastReply}`,
              kind: "reply",
              title: `New reply on ${t.public_id}`,
              detail: t.contact_reason?.trim() || "The player wrote back.",
              to: `/app/support/tickets/${t.id}`,
              at: Date.now(),
              read: false,
              ...(t.priority ? { priority: t.priority } : {}),
            });
          }
          next[t.id] = lastReply;
        }
        if (!prev.primed.tickets) fresh.splice(0, fresh.length);
        tickets = next;
        primed.tickets = true;
      } catch (e) {
        console.warn("[staff-alerts] support poll failed", e);
      }

      // ── Business applications ──
      try {
        const list = await businessesApi.list({ status: "applied", limit: 50 });
        const seen = new Set(prev.applications);
        if (prev.primed.applications) {
          for (const b of list.businesses) {
            if (!seen.has(b.id)) {
              fresh.push({
                key: `application:${b.id}`,
                kind: "application",
                title: "New business application",
                detail: b.display_name,
                to: `/app/businesses/${b.id}`,
                at: Date.now(),
                read: false,
              });
            }
          }
        }
        applications = list.businesses.map((b) => b.id);
        primed.applications = true;
      } catch (e) {
        console.warn("[staff-alerts] applications poll failed", e);
      }

      // ── Trust & Safety reports ──
      try {
        const list = await deskApi.listReports({ view: "open", limit: 100 });
        const seen = new Set(prev.reports);
        if (prev.primed.reports) {
          for (const r of list.reports) {
            if (seen.has(r.id)) continue;
            const urgent = priorityRank(r.priority) >= 2;
            const what = SUBJECT_LABEL[r.subject_type] ?? "Content";
            fresh.push({
              key: `report:${r.id}`,
              kind: "report",
              title: `${urgent ? `${r.priority[0]!.toUpperCase()}${r.priority.slice(1)}-priority report` : "New report"}: ${r.reason_label ?? reasonLabel(r.reason_code)}`,
              detail: `${what}${r.reported_user?.handle ? ` by @${r.reported_user.handle.replace(/^@/, "")}` : ""}${r.subject_preview?.text ? ` · “${r.subject_preview.text.slice(0, 80)}”` : ""}`,
              to: `/app/trust-safety/reports/${r.id}`,
              at: Date.now(),
              read: false,
              priority: r.priority,
            });
          }
        }
        // Keep ids already seen even if resolved since, so a report that
        // leaves and re-enters the open view is not announced twice.
        reports = Array.from(new Set([...list.reports.map((r) => r.id), ...prev.reports])).slice(0, 500);
        primed.reports = true;
      } catch (e) {
        console.warn("[staff-alerts] reports poll failed", e);
      }

      const known = new Set(snapshot.current.alerts.map((a) => a.key));
      const added = sortAlerts(fresh.filter((a) => !known.has(a.key)));

      commit({
        primed,
        tickets,
        applications,
        reports,
        alerts: [...added, ...snapshot.current.alerts].slice(0, MAX_ALERTS),
      });

      if (added.length > 0) {
        const urgent = added.some(isUrgent);
        playAlertChime(urgent);
        // Pop-ups: a few individually, then one summary.
        added.slice(0, 3).forEach((a) =>
          toast.push({ title: a.title, description: a.detail, tone: a.kind === "report" && isUrgent(a) ? "danger" : a.kind === "report" ? "warning" : "info" }));
        if (added.length > 3) {
          toast.push({ title: `${added.length - 3} more new items`, description: "Open Notifications to see them.", tone: "info" });
        }
        const top = added[0]!;
        desktopNotify(
          added.length > 1 ? `${top.title} (+${added.length - 1} more)` : top.title,
          top.detail,
          () => void navigate(top.to),
        );
      }
    } finally {
      polling.current = false;
      if (again.current) {
        again.current = false;
        void poll();
      }
    }
  }, [supportApi, businessesApi, deskApi, commit, toast, navigate]);

  useEffect(() => {
    void poll();
    // Deliberately NOT paused in a background tab (browsers throttle it to
    // about once a minute there, which is still an alert).
    const timer = window.setInterval(() => void poll(), POLL_MS);
    const onVisible = () => { if (document.visibilityState === "visible") void poll(); };
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      window.clearInterval(timer);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [poll]);

  // Live signals: re-read right away (debounced, so a burst is one read).
  useEffect(() => {
    let t: number | undefined;
    const off = subscribeStaffSignals((s) => {
      if (s.topic === "hq-support" && s.event === "ticket_status") return;
      window.clearTimeout(t);
      t = window.setTimeout(() => void poll(), 400);
    });
    return () => { window.clearTimeout(t); off(); };
  }, [poll]);

  const unread = alerts.filter((a) => !a.read).length;
  const urgentUnread = alerts.filter((a) => !a.read && a.kind === "report" && isUrgent(a)).length;

  // The tab title carries the count, so a reviewer on another tab sees it.
  useEffect(() => {
    const base = document.title.replace(/^\(\d+\+?\)\s*/, "");
    document.title = unread > 0 ? `(${unread > 99 ? "99+" : unread}) ${base}` : base;
  }, [unread]);

  const markAllRead = useCallback(() => {
    commit({ ...snapshot.current, alerts: snapshot.current.alerts.map((a) => ({ ...a, read: true })) });
  }, [commit]);

  const markRead = useCallback((key: string) => {
    commit({
      ...snapshot.current,
      alerts: snapshot.current.alerts.map((a) => (a.key === key ? { ...a, read: true } : a)),
    });
  }, [commit]);

  const clear = useCallback(() => {
    commit({ ...snapshot.current, alerts: [] });
  }, [commit]);

  return { alerts, unread, urgentUnread, markRead, markAllRead, clear };
}
